import React, {useContext, useEffect, useState} from 'react';
import {injectIntl} from "react-intl";
import { useDataLayer } from "dataLayerHelper";
import ListItem from "@Components/ListItem";
import Loading from "@Components/Loading";
import SectionHeader from "@Components/SectionHeader";
import Modal from "@Components/Modal";
import Button from "@FormElements/Button";
import Form from 'form';
import FormContext from "formContext";
import axios from "axios";
import atomeFieldMapping from "@Utils/atomeFieldsHelper";
import Logger from '@ffw/logger-lib';
import {Helmet} from 'rnd-helmet';
import SubMenu from "@Components/SubMenu";
import useReplaceInitials from "useReplaceInitials";
import PersonalDataInfo from "@Components/PersonalDataInfo";

function PersonalInformation({intl, staticContext, match}) {
  const {
    formData,
    setFormData,
    handleErrors,
    currentStep,
    getFormSchema,
    setFormSchema,
    showToast,
    setToastMessage,
    setErrors,
    additionalData,
    setAdditionalData,
    timesRendered,
    triggerRerender,
    handleChange
  } = useContext(FormContext);
  const {user} = additionalData;
  const {dsarSettings} = additionalData
  const {locale} = intl;
  const nameDetailsForm = "name-details";
  const personalDetailsForm = "personal-details";
  const contactDetailsForm = "contact-details";
  const addressDetailsForm = "address-details";
  const resumeForm = "user-resume"; //cvDetailsForm
  const graduationForm = "user-graduation";
  const candidateForm = "candidate-profile"
  const [formName, setFormName] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const {
    titleMapping,
    experienceMapping,
    studiesLevelMapping,
    disabledWorkerMapping
  } = atomeFieldMapping(intl);

  // Generate dataLayer for the page.
  useDataLayer(locale, staticContext);

  useEffect(() => {
    if (window.__ROUTE_DATA__) {
      delete window.__ROUTE_DATA__;
    }
    else {
      axios.get(`${process.env.REACT_APP_API_PREFIX}/user-data?currentLanguage=${locale}`)
        .then(response => {
          if (response.data.redirect === true && response.data.location) {
            window.location.href = response.data.location;
          }
          else {
            setAdditionalData(prevState => ({
              ...prevState,
              ...response.data
            }));
          }
        });
    }
  }, [timesRendered]);

  useEffect(() => {
    // Enable checkbox for Apply only if new file has been uploaded.
    let schema = getFormSchema(resumeForm);
    const availableFromIndex = schema[currentStep].data[0].components.findIndex(item => {
      if (item.settings && item.settings.name && item.settings.name === 'Apply') {
        return item
      }
    });

    let checkboxDisabled = (user && user.hasOwnProperty('Apply') && user.Apply === 'yes');

    if (formData[currentStep] && formData[currentStep].CV && formData[currentStep].CV.token) {
      checkboxDisabled = false;

      if (schema[currentStep].data[0].components[availableFromIndex].settings.state && schema[currentStep].data[0].components[availableFromIndex].settings.state.hasOwnProperty('Apply')) {
        delete schema[currentStep].data[0].components[availableFromIndex].settings.state.Apply;
      }
    }

    schema[currentStep].data[0].components[availableFromIndex].settings.disabled = checkboxDisabled;

    setFormSchema(resumeForm, schema);
  }, [formData[currentStep], modalOpen]);

  if (!user) {
    return <Loading/>;
  }

  const handleSubmit = event => {
    event.preventDefault();

    setLoading(true);
    let submitApiPath;
    switch (formName) {
      case nameDetailsForm:
        submitApiPath = '/user/name-details/';
        break;
      case personalDetailsForm:
        submitApiPath = '/user/personal-details/';
        break;
      case contactDetailsForm:
        submitApiPath = '/user/contact-details/';
        break;
      case addressDetailsForm:
        submitApiPath = '/user/address-details/';
        break;
      case resumeForm:
        submitApiPath = '/user/resume/';
        break;
      case graduationForm:
        submitApiPath = "/user/education-details/"
        break;
      case candidateForm:
        submitApiPath = '/user/work-xp/';
        break;

    }
    if (!submitApiPath) {
      Logger.info(`Unable to update data. Missing API path for ${formName} form`, "Profile/profile-personal-details");
      return null;
    }

    axios.put(`${process.env.REACT_APP_API_PREFIX}${submitApiPath}`, formData[currentStep])
      .then(response => {
        if (response.status >= 200 && response.status < 300) {
          if (window && window.initAuthWidget && window.myRandClient && submitApiPath === "/user/name-details/") {
            // Name might have changed. Fetch refresh-token, which will also update MyRandInfo cookie, and
            // then replace user names and initials in the header.
            const replaceInitials = useReplaceInitials(formData[currentStep]);
            window.myRandClient.refreshToken(replaceInitials);
          }
          setModalOpen(false);
          setFormData([]);
          setErrors();
          setLoading(false);
          setTimeout(() => {
            const message = intl.formatMessage({id: 'Profile.AccountUpdated.Toast.SuccessMessage'});
            setToastMessage(message);
            showToast();
          }, 500);
          triggerRerender();
        }
      })
      .catch(error => {
        setLoading(false);
        handleErrors(error.response.data);
      });
  };

  const onCloseModal = () => {
    setFormData([]);
    setErrors();
    setFormName('');
    setModalOpen(false)
  };

  const handleEditNameDetails = () => {
    let dataForForm = {};
    if (process.env.REACT_APP_FIELD_TITLE === "true") {
      dataForForm.TitleID = user.atomeData.TitleID;
    }
    if (process.env.REACT_APP_FIELD_FIRST_NAME === "true") {
      dataForForm.FirstName = user.atomeData.FirstName;
    }
    if (process.env.REACT_APP_FIELD_MIDDLE_NAME === "true") {
      dataForForm.MiddleName = user.atomeData.MiddleName;
    }
    if (process.env.REACT_APP_FIELD_LAST_NAME === "true") {
      dataForForm.LastName = user.atomeData.LastName;
    }
    dataForForm.Optin = '';

    setFormData([dataForForm]);
    setFormName(nameDetailsForm);
    setModalOpen(true);
  };

  const handleEditPersonalDetails = () => {
    let dataForForm = {};
    if (process.env.REACT_APP_FIELD_DATE_OF_BIRTH === "true") {
      dataForForm.date_of_birth = user.date_of_birth;
    }
    if (process.env.REACT_APP_FIELD_FISCAL_NUMBER === "true") {
      dataForForm.NIF = user.NIF;
    }
    if (process.env.REACT_APP_FIELD_NATIONALITY === "true") {
      dataForForm.nationality = user.nationality;
    }
    if (process.env.REACT_APP_FIELD_GENDER === "true") {
      dataForForm.gender = user.gender;
    }

    setFormData([dataForForm]);
    setFormName(personalDetailsForm);
    setModalOpen(true);
  };

  const handleEditContactDetails = () => {
    let dataForForm = {};
    if (process.env.REACT_APP_FIELD_PHONE === "true") {
      dataForForm.Phone = user.Phone;
    }
    if (process.env.REACT_APP_FIELD_EMAIL === "true") {
      dataForForm.email = user.email;
    }
    if (process.env.REACT_APP_FIELD_MOBILE === "true") {
      dataForForm.MobilePhone = user.MobilePhone;
    }
    if (process.env.REACT_APP_FIELD_MARKETING_EMAILS === "true") {
      dataForForm.subscribe_marketing = user.subscribe_marketing;
    }

    setFormData([dataForForm]);
    setFormName(contactDetailsForm);
    setModalOpen(true);
  };

  const handleEditAddressDetails = () => {
    let dataForForm = {};
    if (process.env.REACT_APP_FIELD_APARTMENT_NUMBER === "true") {
      dataForForm.ApartmentNumber = user.atomeData.Location.ApartmentNumber;
    }
    if (process.env.REACT_APP_FIELD_BLOCK === "true") {
      dataForForm.Block = user.atomeData.Location.Block;
    }
    if (process.env.REACT_APP_FIELD_STREET === "true") {
      dataForForm.Street = user.atomeData.Location.Street;
    }
    if (process.env.REACT_APP_FIELD_PLACE_NAME === "true") {
      dataForForm.PlaceName = user.atomeData.Location.PlaceName;
    }
    if (process.env.REACT_APP_FIELD_POSTAL_CODE === "true") {
      dataForForm.PostalCode = user.atomeData.Location.PostalCode;
    }
    if (process.env.REACT_APP_FIELD_COUNTRY === "true") {
      dataForForm.CountryName = user.atomeData.Location.CountryName;
    }
    dataForForm.CountryID = 78;

    setFormData([dataForForm]);
    setFormName(addressDetailsForm);
    setModalOpen(true);
  };

  const handleEditResume = () => {
    let dataForForm = {};
    if (process.env.REACT_APP_FIELD_CV === "true") {
      dataForForm.CV = user.CV;
      dataForForm.ResumeID = user.atomeData.Resume && user.atomeData.Resume.ResumeID ? user.atomeData.Resume.ResumeID : null

      dataForForm.files = [user.CV];
    }
    if (process.env.REACT_APP_FIELD_DOCUMENTS === "true") {
      dataForForm.documents = user.documents;
    }

    if (user.Apply === 'yes') {
      dataForForm.Apply = 'yes';
    }


    setFormData([dataForForm]);
    setFormName(resumeForm);
    setModalOpen(true);
  };

  const handleEditCandidate = () => {
    let dataForForm = {};
    if (process.env.REACT_APP_FIELD_LEVEL_OF_STUDY === "true") {
      dataForForm.LevelOfStudiesID = user.atomeData.hasOwnProperty('LevelOfStudies') && user.atomeData['LevelOfStudies'] ? user.atomeData['LevelOfStudies'][0].Key : '';
    }
    if (process.env.REACT_APP_FIELD_PROFESSIONAL_EXPERIENCE === "true") {
      dataForForm.ExperienceTimeID = user.atomeData.hasOwnProperty('ExperienceTime') && user.atomeData['ExperienceTime'] ? user.atomeData['ExperienceTime'][0].Key : '';
    }
    if (process.env.REACT_APP_FIELD_HANDICAPPED_WORKER === "true") {
      dataForForm.IsHandicapped = `${user.atomeData.IsHandicapped}`;
    }

    setFormData([dataForForm]);
    setFormName(candidateForm);
    setModalOpen(true);
  }

  const modalFooter = () => {
    const modalButtonSettings = {
      medium: true,
      filled: true,
      fullWidth: true,
      text: getFormSchema(formName)[currentStep].modalButton,
      loading
    }

    return <Button onClick={handleSubmit} settings={modalButtonSettings} />
  }

  const renderModal = () => {
    if (!modalOpen || !formName) {
      return '';
    }

    return (
      <Modal
        title={intl.formatMessage({id: getFormSchema(formName)[currentStep].modalTitle})}
        onClose={onCloseModal}
        footer={modalFooter()}
        footerDivider={true}
      >
        <Form name={formName} handleChange={handleChange} state={formData[currentStep]} handleSubmit={handleSubmit}/>
      </Modal>
    );
  };

  const renderPhoto = () => {
    if (!user || !user.photo || !user.photo.url) {
      return '';
    }
    return (
      <div className="profile__photo">
        <img alt="profile photo" src={`${user.photo.url}`}/>
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>{intl.formatMessage({id: 'Menu.Profile'})} | {intl.formatMessage({id: 'Breadcrumb.Dashboard'})}</title>
        <header className="header bg-variant-white header--s header--welcome"/>
      </Helmet>
      <div className="header bg-brand--white header--s header--welcome">
        <SubMenu />
      </div>
      <div className="block__wrapper wrapper pt-l l:pt-xl">
        <SectionHeader title={intl.formatMessage({id: 'Profile.PersonalDetails.Section.Title'})} description={intl.formatMessage({id: 'PersonalInformation.Description'})}/>
        <div className="block__content block__content--align-right block__content--s">
          <ul className="list-items">
            {
              process.env.REACT_APP_SECTION_NAME_DETAILS === "true" &&
              <ListItem
                title={intl.formatMessage({id: 'PersonalInformation.Name.List.Title'})}
                handleEdit={handleEditNameDetails}
                label={intl.formatMessage({id: 'PersonalInformation.Name.List.Title'})}
                divider="true"
              >
                <div className="avatar-info mt-s l:mt-m">
                  <div className="avatar avatar--XL avatar__initials mr-m">
                    {process.env.REACT_APP_FIELD_PHOTO === "true" && renderPhoto()}
                    <span>{user?.atomeData?.FirstName?.charAt(0)}{user?.atomeData?.LastName?.charAt(0)}</span>
                  </div>
                  <div className="avatar-info__name mt-xs l:mt-none">
                    {process.env.REACT_APP_FIELD_TITLE === "true" && titleMapping[user.atomeData.TitleID]} {process.env.REACT_APP_FIELD_FIRST_NAME === "true" && user.atomeData.FirstName ? user.atomeData.FirstName : '-'} {process.env.REACT_APP_FIELD_MIDDLE_NAME === "true" && user.atomeData.MiddleName ? user.atomeData.MiddleName : ''} {process.env.REACT_APP_FIELD_LAST_NAME === "true" && user.atomeData.LastName ? user.atomeData.LastName : '-'}
                  </div>
                </div>
              </ListItem>
            }
            {
              process.env.REACT_APP_SECTION_PERSONAL_DETAILS === "true" &&
              <ListItem
                title={intl.formatMessage({id: 'PersonalInformation.PersonalDetails.List.Title'})}
                handleEdit={handleEditPersonalDetails}
                label={intl.formatMessage({id: 'PersonalInformation.PersonalDetails.List.Title'})}
                divider="true"
              >
                {
                  process.env.REACT_APP_FIELD_GENDER === "true" &&
                  <div className="my-environment-item__list my-environment-item__list-details mt-s l:mt-xs">
                    <div className="my-environment-item__list-title text--alternative">{intl.formatMessage({id: 'PersonalInformation.PersonalDetails.Gender.Label'})}</div>
                    <div className="my-environment-item__list-content mt-xxs l:mt-none">{(user.gender ? user.gender : '-')}</div>
                  </div>
                }
              </ListItem>
            }
            {
              process.env.REACT_APP_SECTION_CONTACT_DETAILS === "true" &&
              <ListItem
                title={intl.formatMessage({id: 'PersonalInformation.ContactDetails.List.Title'})}
                handleEdit={handleEditContactDetails}
                label={intl.formatMessage({id: 'PersonalInformation.ContactDetails.List.Title'})}
                divider="true"
              >
                {
                  process.env.REACT_APP_FIELD_PHONE === "true" &&
                  <div className="my-environment-item__list my-environment-item__list-details mt-s l:mt-xs">
                    <div
                      className="my-environment-item__list-title text--alternative">{intl.formatMessage({id: 'PersonalInformation.ContactDetails.Phone.Label'})}</div>
                    <div
                      className="my-environment-item__list-content mt-xxs l:mt-none">{user.atomeData.Phone && user.atomeData.hasOwnProperty('Phone') ? user.atomeData.Phone : '-'}</div>
                  </div>
                }
                {
                  process.env.REACT_APP_FIELD_EMAIL === "true" &&
                  <div className="my-environment-item__list my-environment-item__list-details mt-s l:mt-xs">
                    <div
                      className="my-environment-item__list-title text--alternative">{intl.formatMessage({id: 'PersonalInformation.ContactDetails.Email.Label'})}</div>
                    <div
                      className="my-environment-item__list-content mt-xxs l:mt-none">{user.atomeData.Email && user.atomeData.hasOwnProperty('Email') ? user.atomeData.Email : '-'}</div>
                  </div>
                }
                {
                  process.env.REACT_APP_FIELD_MOBILE === "true" &&
                  <div className="my-environment-item__list my-environment-item__list-details mt-s l:mt-xs">
                    <div
                      className="my-environment-item__list-title text--alternative">{intl.formatMessage({id: 'PersonalInformation.ContactDetails.MobilePhone.Label'})}</div>
                    <div
                      className="my-environment-item__list-content mt-xxs l:mt-none">{(user.atomeData.MobilePhone && user.atomeData.hasOwnProperty('MobilePhone') ? user.atomeData.MobilePhone : '-')}</div>
                  </div>
                }
                {
                  process.env.REACT_APP_FIELD_MARKETING_EMAILS=== "true" &&
                  <div className="my-environment-item__list my-environment-item__list-details mt-s l:mt-xs">
                    <div
                      className="my-environment-item__list-title text--alternative">{intl.formatMessage({id: 'PersonalInformation.ContactDetails.Marketing.Label'})}</div>
                    <div
                      className="my-environment-item__list-content mt-xxs l:mt-none">{(user.subscribe_marketing ? user.subscribe_marketing : '-')}</div>
                  </div>
                }
              </ListItem>
            }
            {
              process.env.REACT_APP_SECTION_ADDRESS_DETAILS === "true" &&
              <ListItem
                title={intl.formatMessage({id: 'PersonalInformation.AddressDetails.List.Title'})}
                handleEdit={handleEditAddressDetails}
                label={intl.formatMessage({id: 'PersonalInformation.AddressDetails.List.Title'})}
                divider="true"
              >
                {
                  process.env.REACT_APP_FIELD_APARTMENT_NUMBER === "true" &&
                  <div className="my-environment-item__list my-environment-item__list-details mt-s l:mt-xs">
                    <div className="my-environment-item__list-title text--alternative">{intl.formatMessage({id: 'PersonalInformation.AddressDetails.ApartmentNumber.Label'})}</div>
                    <div className="my-environment-item__list-content mt-xxs l:mt-none">{(user.atomeData.Location && user.atomeData.Location.hasOwnProperty('ApartmentNumber') && user.atomeData.Location.ApartmentNumber) ? user.atomeData.Location.ApartmentNumber : '-'}</div>
                  </div>
                }
                {
                  process.env.REACT_APP_FIELD_BLOCK === "true" &&
                  <div className="my-environment-item__list my-environment-item__list-details mt-s l:mt-xs">
                    <div className="my-environment-item__list-title text--alternative">{intl.formatMessage({id: 'PersonalInformation.AddressDetails.Residence.Label'})}</div>
                    <div className="my-environment-item__list-content mt-xxs l:mt-none">{(user.atomeData.Location && user.atomeData.Location.hasOwnProperty('Block') && user.atomeData.Location.Block) ? user.atomeData.Location.Block : '-'}</div>
                  </div>
                }
                {
                  process.env.REACT_APP_FIELD_STREET === "true" &&
                  <div className="my-environment-item__list my-environment-item__list-details mt-s l:mt-xs">
                    <div className="my-environment-item__list-title text--alternative">{intl.formatMessage({id: 'PersonalInformation.AddressDetails.StreetDetails.Label'})}</div>
                    <div className="my-environment-item__list-content mt-xxs l:mt-none">{(user.atomeData.Location && user.atomeData.Location.hasOwnProperty('Street') && user.atomeData.Location.Street) ? user.atomeData.Location.Street : '-'}</div>
                  </div>
                }
                {
                  process.env.REACT_APP_FIELD_PLACE_NAME === "true" &&
                  <div className="my-environment-item__list my-environment-item__list-details mt-s l:mt-xs">
                    <div className="my-environment-item__list-title text--alternative">{intl.formatMessage({id: 'PersonalInformation.AddressDetails.Locality.Label'})}</div>
                    <div className="my-environment-item__list-content mt-xxs l:mt-none">{(user.atomeData.Location && user.atomeData.Location.hasOwnProperty('PlaceName') && user.atomeData.Location.PlaceName) ? user.atomeData.Location.PlaceName : '-'}</div>
                  </div>
                }
                {
                  process.env.REACT_APP_FIELD_POSTAL_CODE === "true" &&
                  <div className="my-environment-item__list my-environment-item__list-details mt-s l:mt-xs">
                    <div className="my-environment-item__list-title text--alternative">{intl.formatMessage({id: 'PersonalInformation.AddressDetails.PostalCode.Label'})}</div>
                    <div className="my-environment-item__list-content mt-xxs l:mt-none">{(user.atomeData.Location && user.atomeData.Location.hasOwnProperty('PostalCode') && user.atomeData.Location.PostalCode) ? user.atomeData.Location.PostalCode : '-'}</div>
                  </div>
                }
                {
                  process.env.REACT_APP_FIELD_CITY === "true" &&
                  <div className="my-environment-item__list my-environment-item__list-details mt-s l:mt-xs">
                    <div className="my-environment-item__list-title text--alternative">{intl.formatMessage({id: 'PersonalInformation.AddressDetails.City.Label'})}</div>
                    <div className="my-environment-item__list-content mt-xxs l:mt-none">{(user.atomeData.Location && user.atomeData.Location.hasOwnProperty('TownName') && user.atomeData.Location.TownName) ? user.atomeData.Location.TownName : '-'}</div>
                  </div>
                }
                {
                  process.env.REACT_APP_FIELD_COUNTRY === "true" &&
                  <div className="my-environment-item__list my-environment-item__list-details mt-s l:mt-xs">
                    <div className="my-environment-item__list-title text--alternative">{intl.formatMessage({id: 'PersonalInformation.AddressDetails.Country.Label'})}</div>
                    <div className="my-environment-item__list-content mt-xxs l:mt-none">{(user.atomeData.Location && user.atomeData.Location.hasOwnProperty('CountryName') && user.atomeData.Location.CountryName) ? user.atomeData.Location.CountryName : '-'}</div>
                  </div>
                }
              </ListItem>
            }
            {
              process.env.REACT_APP_SECTION_CV_DOCUMENTS === "true" &&
              <ListItem
                title={intl.formatMessage({id: 'PersonalInformation.ResumeAndDocuments.List.Label'})}
                handleEdit={handleEditResume}
                label={intl.formatMessage({id: 'PersonalInformation.ResumeAndDocuments.List.Label'})}
                divider="true"
              >
                {
                  process.env.REACT_APP_FIELD_CV === "true" &&
                  <div className="my-environment-item__list my-environment-item__list-details mt-s l:mt-xs">
                    <div className="my-environment-item__list-title text--alternative">{intl.formatMessage({id: 'PersonalInformation.ResumeAndDocuments.Resume.Label'})}</div>
                    <div className="my-environment-item__list-content mt-xxs l:mt-none">
                      {user.CV && user.CV.url && user.CV.filename
                        ? <a href="#" rel="noopener noreferrer" onClick={e => e.preventDefault()}>{user.CV.filename}</a>
                        : '-'
                      }
                    </div>
                  </div>
                }
                {
                  process.env.REACT_APP_FIELD_CV === "true" &&
                  <div className="my-environment-item__list my-environment-item__list-details mt-s l:mt-xs">
                    <div className="my-environment-item__list-title text--alternative">{intl.formatMessage({id: 'PersonalInformation.ResumeAndDocuments.SendToAgencies.Label'})}</div>
                    <div className="my-environment-item__list-content mt-xxs l:mt-none">
                      {user.CV && user.CV.url && user.CV.filename
                        ? (user.Apply && (user.Apply === 'yes'))
                          ? intl.formatMessage({id: 'PersonalInformation.ResumeAndDocuments.Sent.Yes'})
                          : intl.formatMessage({id: 'PersonalInformation.ResumeAndDocuments.Sent.No'})
                        : '-'
                      }
                    </div>
                  </div>
                }
              </ListItem>
            }
            {
              process.env.REACT_APP_SECTION_CANDIDATE === "true" &&
              <ListItem
                title={intl.formatMessage({id: 'PersonalInformation.CandidateProfile.List.Title'})}
                handleEdit={handleEditCandidate}
                label={intl.formatMessage({id: 'PersonalInformation.CandidateProfile.List.Title'})}
                divider="true"
              >
                {
                  process.env.REACT_APP_FIELD_LEVEL_OF_STUDY === "true" &&
                  <div className="my-environment-item__list my-environment-item__list-details mt-s l:mt-xs">
                    <div className="my-environment-item__list-title text--alternative">{intl.formatMessage({id: 'PersonalInformation.CandidateProfile.StudyLevel.Label'})}</div>
                    <div className="my-environment-item__list-content mt-xxs l:mt-none">{(user.atomeData.LevelOfStudies && user.atomeData.hasOwnProperty('LevelOfStudies') && user.atomeData.LevelOfStudies[0]) ? studiesLevelMapping[user.atomeData.LevelOfStudies[0].Key] : '-'}</div>
                  </div>
                }
                {
                  process.env.REACT_APP_FIELD_PROFESSIONAL_EXPERIENCE === "true" &&
                  <div className="my-environment-item__list my-environment-item__list-details mt-s l:mt-xs">
                    <div className="my-environment-item__list-title text--alternative">{intl.formatMessage({id: 'PersonalInformation.CandidateProfile.ProfessionalExperience.Label'})}</div>
                    <div className="my-environment-item__list-content mt-xxs l:mt-none">{(user.atomeData.ExperienceTime && user.atomeData.hasOwnProperty('ExperienceTime') && user.atomeData.ExperienceTime[0]) ? experienceMapping[user.atomeData.ExperienceTime[0].Key] : '-'}</div>
                  </div>
                }
                {
                  process.env.REACT_APP_FIELD_HANDICAPPED_WORKER === "true" &&
                  <div className="my-environment-item__list my-environment-item__list-details mt-s l:mt-xs">
                    <div className="my-environment-item__list-title text--alternative">{intl.formatMessage({id: 'PersonalInformation.CandidateProfile.DisabledWorker.Label'})}</div>
                    <div className="my-environment-item__list-content mt-xxs l:mt-none">{(user.atomeData.hasOwnProperty('IsHandicapped')) ? disabledWorkerMapping[user.atomeData.IsHandicapped] : '-'}</div>
                  </div>
                }
              </ListItem>
            }
            {process.env.REACT_APP_DSAR_REQUEST === "true" &&
                <PersonalDataInfo dsarSettings={dsarSettings} />
            }
          </ul>
        </div>
      </div>
      {renderModal()}
    </>
  )
}

export default injectIntl(PersonalInformation);
